import { Component, HostBinding, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { Enterprise } from '@app/core/interfaces/enterprise';
import { UserLevel, userLevelIsEnough } from '@app/core/models/user-level';
import { ContextSwitcherComponent } from '@app/menu/components/context-switcher/context-switcher.component';
import { MenuData } from '@app/menu/models/menu-data';
import { MenuService } from '@app/menu/services/menu.service';
import { RouterService } from '@app/routing/router.service';
import { EnterpriseContextService } from '@app/shared/services/context/enterprise-context.service';
import { ServiceProviderContextService } from '@app/shared/services/context/service-provider-context.service';
import { RouteDetectionService } from '@app/shared/services/route-detection.service';
import { TypeaheadEntry } from '@app/shared/services/typeahead-basket.service';
import { sortBy } from '@app/utils/common';
import { equals } from '@app/utils/equals';
import { StringMap } from '@app/utils/string-map';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'portal-enterprise-switcher',
  templateUrl: './enterprise-switcher.component.html',
  styles: [],
  standalone: false,
})
export class EnterpriseSwitcherComponent implements OnInit {
  @HostBinding('class')
  class: string;

  @ViewChild('switcher', { read: ContextSwitcherComponent })
  switcher: ContextSwitcherComponent;

  entries$: Observable<TypeaheadEntry[]>;
  enterprise$: Observable<Enterprise>;

  isAllowedToSwitch: boolean;
  isOnEnterpriseLevel: boolean;
  menuData: MenuData;
  selectedEntryValue: string;

  private alternateRoutingMap: StringMap<string> = {};

  /**
   * Observable that emits only if either one of the route parameters serviceProviderId, enterpriseId or userId has changed
   */
  private menu$ = this.menuService.menuData$.pipe(
    distinctUntilChanged((a, b) =>
      equals([a.serviceProviderId, a.enterpriseId, a.userId], [b.serviceProviderId, b.enterpriseId, b.userId])
    )
  );

  constructor(
    private activatedRoute: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private enterpriseContextService: EnterpriseContextService,
    private menuService: MenuService,
    private routeDetectionService: RouteDetectionService,
    private routerService: RouterService,
    private serviceProviderContextService: ServiceProviderContextService
  ) {}

  ngOnInit() {
    this.enterprise$ = this.enterpriseContextService.enterprise.data$.pipe(untilDestroyed(this));

    // Fetch the current enterprise and remember it, so we can highlight it
    this.menu$
      .pipe(
        filter((menuData) => menuData.enterpriseId !== this.selectedEntryValue),
        untilDestroyed(this)
      )
      .subscribe((menuData) => {
        this.menuData = menuData;

        const enterpriseId: string | undefined = menuData.enterpriseId;

        const isEnterpriseOrHigher = userLevelIsEnough(
          UserLevel.EnterpriseAdmin,
          this.authenticationService.authUser
        );

        this.isAllowedToSwitch = this.authenticationService.userIsServiceProviderAdmin();

        this.selectedEntryValue = enterpriseId;
        this.isOnEnterpriseLevel = enterpriseId !== undefined;

        if (this.isOnEnterpriseLevel && isEnterpriseOrHigher) {
          this.class = 'main-nav__enterprise-switcher';

          if (this.isAllowedToSwitch) {
            this.entries$ = this.serviceProviderContextService.enterprises.data$.pipe(
              untilDestroyed(this),
              map((enterprises) =>
                enterprises
                  .map((enterprise): TypeaheadEntry => {
                    return {
                      key: `${enterprise.name} ${enterprise.description}`,
                      label: `${enterprise.name} <small>${enterprise.description}</small>`,
                      meta: {
                        enterprise,
                      },
                      value: enterprise.name,
                    };
                  })
                  .sort(sortBy('key'))
              )
            );
          }
        } else {
          this.class = 'main-nav__enterprise-switcher d-none';
        }
      });
  }

  toggle(): void {
    this.switcher.toggle();
  }

  /**
   * Close the dropdown and dispatch a SwitchCurrentRoute event
   * @param typeaheadEntry
   */
  trigger(typeaheadEntry: TypeaheadEntry) {
    if (typeaheadEntry.value !== this.selectedEntryValue) {
      const alternateRoute = this.alternateRoutingMap[this.routerService.currentRouteData.name];

      // Check if an alternate route has been defined for the current page
      if (alternateRoute !== undefined) {
        // Resolve and navigate to it
        const route = this.routerService.resolveLink(alternateRoute, {
          enterpriseId: typeaheadEntry.value,
        });

        this.routerService.navigateByUrl(route.url);
      } else {
        const enterpriseName = typeaheadEntry.meta.enterprise.name;

        // Switch the current route
        this.menuService.switch('enterpriseId', {
          enterpriseId: typeaheadEntry.value,
          enterpriseName: enterpriseName,
        });
      }
    }
  }
}
